import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CaseService } from '../../services/case.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-invoice-view',
  templateUrl: './invoice-view.component.html',
  styleUrls: ['./invoice-view.component.scss'],
})
export class InvoiceViewComponent implements OnInit {

  @Input() selectedProfile: any = {
    currency_symbol: '$' // Default currency symbol
  };
  caseId: string;
  invoiceList: any[] = [];
  isLoading = true;
  errorMessage: string;
  title = 'Lateral Debtor Portal';
  pageMode: 'invoice' | 'application' = 'invoice';
  isTenant = false;
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private caseService: CaseService,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    // Get the encoded case ID from the route parameter
    const encodedCaseId = this.route.snapshot.paramMap.get('caseid');

    // Check if case ID is present and not empty
    if (encodedCaseId && encodedCaseId.trim() !== '') {
      try {
        // Safely decode the case ID
        this.caseId = atob(encodedCaseId);

        if (this.caseId && this.caseId.trim() !== '') {
          // Load invoices only if decoded value is valid
          this.loadInvoices();
        } else {
          throw new Error('Decoded case ID is empty.');
        }

      } catch (error) {
        console.error('Error decoding case ID:', error);
        this.errorMessage = this.translate.instant('INVOICE.INVALID_CASE_ID');
        this.isLoading = false;
      }
    } else {
      console.warn('Case ID parameter is missing in URL.');
      this.errorMessage = this.translate.instant('INVOICE.MISSING_CASE_ID');
      this.isLoading = false;
    }
  }


  loadInvoices() {
    if (!this.caseId?.trim()) {
      this.errorMessage = this.translate.instant('INVOICE.INVALID_CASE_ID');
      this.invoiceList = [];
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    this.errorMessage = null;

    this.caseService.getInvoices(this.caseId).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        const payload = res?.data ?? res;
        const rows: any[] = payload?.result && payload?.cases ? (Object.values(payload.cases) as any[]) : [];

        this.pageMode = this.resolvePageMode(rows);
        this.title = this.resolveTitle();
        this.isTenant = payload?.isTenant ?? false;
        this.invoiceList = rows
          .filter((row: any) => this.matchesPageMode(row?.document_type))
          .map((row: any) => {
            const fromDate = this.formatDate(row?.offense_date_from);
            const toDate = this.formatDate(row?.offense_date_to);
            const description = row?.description || '-';
            return {
              ...row,
              display_date: fromDate,
              display_description: (fromDate !== '-' && toDate !== '-')
                ? `${description}\n${fromDate} - ${toDate}`
                : description,
              paid_display: this.formatAmount(row?.paid, row?.currency_symbol),
              net_display: this.formatAmount(row?.net, row?.currency_symbol),
              vat_display: this.formatAmount(row?.vat, row?.currency_symbol),
              amount_display: this.formatAmount(row?.amount, row?.currency_symbol),
              total_outstanding_display: this.formatAmount(row?.total_outstanding, row?.currency_symbol),
            };
          });

        if (!this.invoiceList.length)
          this.errorMessage = this.translate.instant('CASE_DETAIL.NO_INVOICES_FOUND');
      },
      error: () => {
        this.errorMessage = this.translate.instant('INVOICE.INVALID_CASE_ID');
        this.invoiceList = [];
        this.isLoading = false;
      }
    });
  }

  detectPageModeFromUrl(): 'invoice' | 'application' {
    const currentUrl = (this.router.url || '').toLowerCase();
    if (currentUrl.includes('/application/view/') || currentUrl.includes('/credit-note-application/view/')) {
      return 'application';
    }
    return 'invoice';
  }

  resolveTitle(): string {
    const currentUrl = (this.router.url || '').toLowerCase();
    if (currentUrl.includes('/credit-note-application/view/')) {
      return 'Credit Note Application Details';
    }

    if (currentUrl.includes('/application/view/')) {
      return 'Application Details';
    }

    if (currentUrl.includes('/credit-note-invoice/view/')) {
      return 'Credit Note Invoice Details';
    }

    return 'Invoice Details';
  }

  matchesPageMode(documentType: string): boolean {
    if (!documentType) {
      return true;
    }
    const type = String(documentType).toLowerCase();
    if (this.pageMode === 'application') {
      return type === 'application' || type === 'credit_note_application';
    }
    return type === 'invoice' || type === 'credit_note_invoice';
  }

  resolvePageMode(rows: any[]): 'invoice' | 'application' {
    const firstType = String(rows?.[0]?.document_type || '').toLowerCase();
    if (firstType.includes('application')) {
      return 'application';
    }

    return 'invoice';
  }

  formatDate(value: any): string {
    if (!value || value === '0000-00-00 00:00:00') {
      return '-';
    }
    const dateString = String(value).split(' ')[0];
    const parts = dateString.split('-');
    if (parts.length !== 3) {
      return '-';
    }
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }

  formatAmount(value: any, symbol: string): string {
    const amount = Number(value);
    const safeAmount = Number.isFinite(amount) ? amount : 0;
    return `${symbol || ''}${safeAmount.toFixed(2)}`;
  }
}
